/**
 * LayerToggleRow — the single shared row used by BOTH the 2D header layers
 * dropdown and the 3D layers panel, so the two menus render from one component
 * and can't drift apart again. See docs/3d-layers-menu-plan.md Phase C.
 *
 * Structure: a row <div> containing a `role="switch"` toggle <button> (icon +
 * label + optional count + switch) and the shared ⓘ LayerInfoButton as a
 * SIBLING (not nested — avoids invalid nested-button HTML; the ⓘ already stops
 * click propagation so it never toggles the layer).
 */

import { ReactNode } from 'react';
import { LayerInfoButton } from './LayerInfoModal';
import './LayerToggleRow.css';

interface LayerToggleRowProps {
  label: string;
  checked: boolean;
  onToggle: (next: boolean) => void;
  icon?: ReactNode;
  /** layerKey for the shared ⓘ info dialog; omit for no ⓘ. */
  infoKey?: string;
  loading?: boolean;
  /** Small count badge (e.g. active aircraft); hidden when 0/undefined. */
  count?: number;
  disabled?: boolean;
  /**
   * Supporter-locked row: renders a 🔒 badge instead of the switch and treats
   * the row as a button. `onToggle` is still fired on click (the parent, e.g.
   * PremiumLayerToggleRow, uses it to open the SupporterModal).
   */
  locked?: boolean;
  /** Badge text shown next to the lock when `locked`. */
  lockedBadge?: string;
}

export default function LayerToggleRow({
  label,
  checked,
  onToggle,
  icon,
  infoKey,
  loading = false,
  count,
  disabled = false,
  locked = false,
  lockedBadge = 'Supporters',
}: LayerToggleRowProps) {
  return (
    <div className={`layer-toggle-row${checked && !locked ? ' active' : ''}${disabled ? ' disabled' : ''}${locked ? ' locked' : ''}`}>
      <button
        type="button"
        className="layer-toggle-row-main"
        // A locked row isn't a working switch — expose it as a plain button so
        // screen readers don't announce a toggle state the user can't set.
        role={locked ? undefined : 'switch'}
        aria-checked={locked ? undefined : checked}
        aria-label={locked ? `${label} — ${lockedBadge} only, tap to learn more` : label}
        disabled={disabled}
        onClick={() => onToggle(!checked)}
      >
        {icon && <span className="layer-toggle-row-icon">{icon}</span>}
        <span className="layer-toggle-row-label">
          {label}
          {loading && <span className="layer-toggle-row-spinner" />}
        </span>
        {typeof count === 'number' && count > 0 && (
          <span className="layer-toggle-row-count">{count}</span>
        )}
        {locked ? (
          <span className="layer-toggle-row-lock">
            <span className="layer-toggle-row-lock-icon" aria-hidden="true">&#x1F512;</span>
            {lockedBadge}
          </span>
        ) : (
          <span className={`layer-toggle-row-switch${checked ? ' on' : ''}`} aria-hidden="true" />
        )}
      </button>
      {infoKey && <LayerInfoButton layerKey={infoKey} />}
    </div>
  );
}
